import React from 'react';
import SupportPromptGroup, { SupportPromptGroupProps } from '@amzn/awsui-chat-components-console/support-prompt-group';
import Box from '@amzn/awsui-components-console/box';
import CopyToClipboard from '@amzn/awsui-components-console/copy-to-clipboard';
import ExpandableSection from '@amzn/awsui-components-console/expandable-section';
import { FileTokenGroupProps } from '@amzn/awsui-components-console/file-token-group';
import Link from '@amzn/awsui-components-console/link';
import Popover from '@amzn/awsui-components-console/popover';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import TextContent from '@amzn/awsui-components-console/text-content';
import { UserInfo } from '../../services/api';

export type Message = ChatBubbleMessage | AlertMessage | RecommendationMessage | SummaryEventMessage;

type ChatBubbleMessage = {
  type: 'chat-bubble';
  authorId: string;
  content: React.ReactNode;
  timestamp: string;
  usingTool?: boolean;
  actions?: 'feedback' | 'code-view';
  hideAvatar?: boolean;
  avatarLoading?: boolean;
  files?: File[];
  supportPrompts?: React.ReactNode;
  showFeedbackDialog?: boolean;
  isPositiveFeedback?: boolean;
  contentToCopy?: string;
  suggestedResponse?: string;
  nextBestActions?: string[];
};

type AlertMessage = {
  type: 'alert';
  content: React.ReactNode;
  header?: string;
};

type RecommendationMessage = {
  type: 'recommendation';
  authorId: string;
  content: React.ReactNode;
  timestamp: string;
  recommendation?: any;
  index?: number;
  // Add optional properties to match ChatBubbleMessage interface
  usingTool?: boolean;
  actions?: 'feedback' | 'code-view';
  hideAvatar?: boolean;
  avatarLoading?: boolean;
  files?: File[];
  supportPrompts?: React.ReactNode;
  showFeedbackDialog?: boolean;
  isPositiveFeedback?: boolean;
  contentToCopy?: string;
};

type SummaryEventMessage = {
  type: 'summaryEvent';
  authorId: string;
  content: React.ReactNode;
  timestamp: string;
  summaryEvent: {
    problemStatement?: string;
    problemStatementExt?: string;
    customer_facing_suggested_response?: string;
    relevantDetails?: string | string[];
    tools_used?: string[];
  };
  index?: number;
};

export const supportPromptItems = [
  {
    text: 'What else can I do with TypeScript?',
    id: 'typescript',
  },
  {
    text: 'How would I add parameters and type checking to this function?',
    id: 'expand',
  },
];
// added as function so that timestamp is evaluated when function is called
export const getInvalidPromptResponse = (): Message => ({
  type: 'chat-bubble',
  authorId: 'gen-ai',
  content: (
    <>
      The interactions and functionality of this demo are limited.
    </>
  ),
  timestamp: new Date().toLocaleTimeString(),
  actions: 'feedback',
  contentToCopy: `The interactions and functionality of this demo are limited.
 1. To see how an incoming response from generative AI is displayed, ask "Show a loading state example".
 2. To see an error alert that appears when something goes wrong, ask "Show an error state example".
 3. To see a how a file upload is displayed, upload one or more files.
 4. To see support prompts, ask "Show support prompts".`,
});

export const getLoadingMessage = (): Message => ({
  type: 'chat-bubble',
  authorId: 'gen-ai',
  content: <Box color="text-status-inactive">Generating a response</Box>,
  timestamp: new Date().toLocaleTimeString(),
  avatarLoading: true,
});

const getFileResponseMessage = (): Message => ({
  type: 'chat-bubble',
  authorId: 'gen-ai',
  content:
    'I see you have uploaded one or more files. I cannot parse the files right now, but you can see what uploaded files look like.',
  timestamp: new Date().toLocaleTimeString(),
  avatarLoading: false,
  actions: 'feedback',
  contentToCopy:
    'I see you have uploaded one or more files. I cannot parse the files right now, but you can see what uploaded files look like.',
});

const getLoadingStateResponseMessage = (): Message => ({
  type: 'chat-bubble',
  authorId: 'gen-ai',
  content: 'That was the loading state. To see the loading state again, ask "Show a loading state example".',
  timestamp: new Date().toLocaleTimeString(),
  avatarLoading: false,
  actions: 'feedback',
  contentToCopy: 'That was the loading state. To see the loading state again, ask "Show a loading state example".',
});

const getErrorStateResponseMessage = (): Message => ({
  type: 'alert',
  header: 'Access denied',
  content: (
    <SpaceBetween size="s">
      <span>
        You don't have permission to [AWSS3:ListBuckets]. To request access, copy the following text and send it to your
        AWS administrator.{' '}
        <Link href="#" external variant="primary">
          Learn more about troubleshooting access denied errors.
        </Link>
      </span>
      <div className="access-denied-alert-wrapper">
        <div className="access-denied-alert-wrapper__box">
          <SpaceBetween size="xxxs">
            <Box variant="code">
              <div>User: [arn:aws:iam::123456789000:user/awsgenericuser]</div>
              <div>Service: [AWSS3]</div>
              <div>Action: [ListBuckets]</div>
              <div>On resource(s): [arn:aws:S3:us-east-1:09876543211234567890]</div>
              <div>Context: [no identity-based policy allows the AWSS3:ListBuckets action.]</div>
            </Box>
          </SpaceBetween>
        </div>
        <div>
          <CopyToClipboard
            copyButtonText="Copy"
            copyErrorText="Text failed to copy"
            copySuccessText="Text copied"
            textToCopy={`User: [arn:aws:iam::123456789000:user/awsgenericuser]
Service: [AWSS3]
Action: [ListBuckets]
On resource(s): [arn:aws:S3:us-east-1:09876543211234567890]
Context: [no identity-based policy allows the AWSS3:ListBuckets action.]
`}
          />
        </div>
      </div>
    </SpaceBetween>
  ),
});

const getSupportPromptResponseMessage = (
  onSupportPromptClick?: (detail: SupportPromptGroupProps.ItemClickDetail) => void,
): Message => ({
  type: 'chat-bubble',
  authorId: 'gen-ai',
  content: (
    <>test</>
  ),
  actions: 'code-view',
  contentToCopy: `// This is the main function that will be executed when the script runs
function main(): void {
  // Use console.log to print "Hello, World!" to the console
  console.log("Hello, World!");
}
// Call the main function to execute the program
main();`,
  timestamp: new Date().toLocaleTimeString(),
  supportPrompts: (
    <SupportPromptGroup
      ariaLabel="Proposed prompts"
      items={supportPromptItems}
      onItemClick={({ detail }) => {
        onSupportPromptClick?.(detail);
      }}
    />
  ),
});

type ValidPromptType = {
  prompt: Array<string>;
  getResponse: (onSupportPromptClick?: (detail: SupportPromptGroupProps.ItemClickDetail) => void) => Message;
};

export const validLoadingPrompts = ['show a loading state example', 'loading state', 'loading'];

export const VALID_PROMPTS: Array<ValidPromptType> = [
  {
    prompt: validLoadingPrompts,
    getResponse: getLoadingStateResponseMessage,
  },
  {
    prompt: ['show an error state example', 'error state', 'error'],
    getResponse: getErrorStateResponseMessage,
  },
  {
    prompt: ['file'],
    getResponse: getFileResponseMessage,
  },
  {
    prompt: ['show support prompts', 'support prompts', 'support prompt'],
    getResponse: onSupportPromptClick => getSupportPromptResponseMessage(onSupportPromptClick),
  },
];

// Needed only for the existing messages upon page load.
function getTimestampMinutesAgo(minutesAgo: number) {
  const d = new Date();
  d.setMinutes(d.getMinutes() - minutesAgo);

  return d.toLocaleTimeString();
}

export type AuthorAvatarProps = {
  type: 'user' | 'gen-ai';
  name: string;
  initials?: string;
  loading?: boolean;
};
type AuthorsType = {
  [key: string]: AuthorAvatarProps;
};

// Utility function to generate user author from UserInfo
export const getUserAuthor = (user: UserInfo): AuthorAvatarProps => {
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  const name = `${firstName} ${lastName}`.trim() || user.username || user.email || 'User';
  const initials = firstName && lastName 
    ? `${firstName[0]}${lastName[0]}`.toUpperCase()
    : name.substring(0, 2).toUpperCase();
  
  return {
    type: 'user',
    name,
    initials
  };
};

// Utility function to get user author ID
export const getUserAuthorId = (user?: UserInfo): string => {
  return user ? `user-${user.userId || 'current'}` : 'user-jane-doe';
};

// Generate AUTHORS with user info
export const getAuthors = (user?: UserInfo): AuthorsType => ({
  [user ? `user-${user.userId || 'current'}` : 'user-jane-doe']: user 
    ? getUserAuthor(user)
    : { type: 'user', name: 'Jane Doe', initials: 'JD' },
  'gen-ai': { type: 'gen-ai', name: 'Generative AI assistant' },
});

// Default AUTHORS for backward compatibility
export const AUTHORS: AuthorsType = {
  'user-jane-doe': { type: 'user', name: 'Jane Doe', initials: 'JD' },
  'gen-ai': { type: 'gen-ai', name: 'Generative AI assistant' },
};

const CitationPopover = ({ count, href }: { count: number; href: string }) => (
  <Box color="text-status-info" display="inline">
    <Popover
      header="Source"
      content={
        <Link href={href} external variant="primary">
          {href}
        </Link>
      }
      position="right"
    >
      [{count}]
    </Popover>
  </Box>
);

export const getInitialMessages = (
  onSupportPromptClick: (detail: SupportPromptGroupProps.ItemClickDetail) => void,
): Array<Message> => {
  return [
   
  ];
};

export const supportPromptMessageOne: Message = {
  type: 'chat-bubble',
  authorId: 'gen-ai',
  content: (
    <>
      TypeScript is a powerful programming language that builds upon JavaScript by adding static typing and other
      features. Here are key things you can do with TypeScript:
      <ol>
        <li>
          Web developement
          <ul>
            <li>Build frontend applications using frameworks like Angular, React, or Vue.js</li>
            <li>Create robust server-side applications with Node.js</li>
            <li>Develop full-stack applications with enhanced type safety</li>
          </ul>
        </li>

        <li>
          Type safety features
          <ul>
            <li>Define explicit types for variables, functions, and objects</li>
            <li>Catch errors during development before runtime</li>
            <li>Use interfaces and type declarations for better code organization</li>
          </ul>
        </li>
        <li>
          Object-oriented programming
          <ul>
            <li>Create classes with proper inheritance</li>
            <li>Implement interfaces</li>
            <li>Use access modifiers (public, private, protected)</li>
          </ul>
        </li>
      </ol>
      TypeScript is particularly valuable for large projects where type safety and code maintainability are important
      considerations.
    </>
  ),
  timestamp: new Date().toLocaleTimeString(),
  actions: 'feedback',
  contentToCopy: `TypeScript is a powerful programming language that builds upon JavaScript by adding static typing and other features. Here are key things you can do with TypeScript:
1. Web developement
 - Build frontend applications using frameworks like Angular, React, or Vue.js
 - Create robust server-side applications with Node.js
 - Develop full-stack applications with enhanced type safety
2. Type safety features
 - Define explicit types for variables, functions, and objects
 - Catch errors during development before runtime
 - Use interfaces and type declarations for better code organization
3. Object-oriented programming
 - Create classes with proper inheritance
 - Implement interfaces
 - Use access modifiers (public, private, protected)
TypeScript is particularly valuable for large projects where type safety and code maintainability are important considerations.`,
};

export const supportPromptMessageTwo: Message = {
  type: 'chat-bubble',
  authorId: 'gen-ai',
  content: (
    <>test</>
  ),
  actions: 'code-view',
  contentToCopy: `// Add input parameters and type checking
function enhancedMain(name: string, greeting: string = "Hello"): void {
  if (!name) {
   throw new error('Name parameter is required.');
  }
  console.log("{greeting}, {name}!");
}
// Call the enhancedMain function to execute the program
enhancedMain('Greetings', 'Earth');`,
  timestamp: new Date().toLocaleTimeString(),
};

export const fileTokenGroupI18nStrings: FileTokenGroupProps.I18nStrings = {
  removeFileAriaLabel: index => `Remove file ${index + 1}`,
  limitShowFewer: 'Show fewer files',
  limitShowMore: 'Show more files',
  errorIconAriaLabel: 'Error',
  warningIconAriaLabel: 'Warning',
};